import React, { useEffect } from 'react';

import { useForm } from 'react-hook-form';
import { Button } from '@mui/material';
import { useToggle } from 'react-use';
import { observer } from 'mobx-react';
import { TitleWithDescription } from '@infoods/lifebase-ui';

import classes from './CoachSeatsWidget.module.scss';
import { InfoBox } from '../../../shared/components/ui-components';
import { ModeButtonsBlock } from '../../../shared/components/buttons';
import { CoachSeats, CoachSeatsFormValues } from '../../interfaces/coachSeats';
import { useMode } from '../../../shared/hooks/useMode';
import { Mode } from '../../../general-types';
import { entityGeneralSetupService } from '../../services/entityGeneralSetupService';
import { CoachSeatsInput } from './CoachSeatsInput/CoachSeatsInput';
import { AddSeatsModal } from '../AddSeatsModal/AddSeatsModal';
import { loggedInUserDataStore } from '../../../shared/stores/loggedInUserDataStore';
import { useUserRoleLabel } from '../../../shared/hooks/useUserRoleLabel';

type CoachSeatsWidgetProps = {
  entityId: number;
  isCoachSeatsCreated: boolean;
  showAddSeatsButton?: boolean;
  coachSeats?: CoachSeats;
  defaultMode?: Mode;
  onSuccessSubmit?: () => void;
};

const CoachSeatsWidget = observer(
  ({
    entityId,
    isCoachSeatsCreated,
    coachSeats,
    showAddSeatsButton = false,
    defaultMode = 'EDIT',
    onSuccessSubmit = () => {},
  }: CoachSeatsWidgetProps) => {
    const { isSuperAdmin } = loggedInUserDataStore;
    const { getForAgent } = useUserRoleLabel();

    const [addSeatsModalOpen, toggleAddSeatsModalOpen] = useToggle(false);

    const { mode, setView, setEdit } = useMode(isSuperAdmin ? defaultMode : 'VIEW');

    const { control, reset, handleSubmit, getValues } = useForm<CoachSeatsFormValues>();

    useEffect(() => {
      if (coachSeats) {
        reset({
          coachSeats: coachSeats.coachSeats,
        });
      }
    }, [coachSeats]);

    const handleCancel = () => {
      setView();

      reset();
    };

    const onSubmit = handleSubmit(async (data) => {
      if (isCoachSeatsCreated) {
        await entityGeneralSetupService.updateCoachSeats(entityId, data);
      } else {
        await entityGeneralSetupService.createCoachSeats(entityId, data);
      }

      reset(data);
      setView();
      onSuccessSubmit();
    });

    const ManageButtons = (
      <ModeButtonsBlock
        mode={mode}
        onEdit={setEdit}
        onCancel={handleCancel}
        onSubmit={onSubmit}
      />
    );

    return (
      <>
        <InfoBox title="General Setup" icon={isSuperAdmin && ManageButtons}>
          <div className={classes.container}>
            <TitleWithDescription
              title="Team Management"
              description={
                <>
                  Expand your team by adding extra {getForAgent({ case: 'lower' })} seats.
                  <br />
                  Additional seats are billed only through GHL
                </>
              }
              titleFont="body-14-bold"
              className={classes.descriptionBlock}
            />
            <div>
              <div className={classes.coachSeatsBlock}>
                <TitleWithDescription
                  title={`${getForAgent()} Seats`}
                  description={`The number of ${getForAgent({ case: 'lower', pluralize: true })} included in your plan`}
                  titleFont="body-14-bold"
                />
                <CoachSeatsInput
                  control={control}
                  coachSeats={getValues().coachSeats}
                  usedSeats={coachSeats?.usedSeats}
                  mode={mode}
                />
              </div>
              {showAddSeatsButton && mode === 'VIEW' && (
                <Button
                  className={classes.addSeatsButton}
                  variant="contained"
                  size="small"
                  onClick={toggleAddSeatsModalOpen}
                >
                  Add Seats
                </Button>
              )}
            </div>
          </div>
        </InfoBox>
        <AddSeatsModal
          open={addSeatsModalOpen}
          onClose={toggleAddSeatsModalOpen}
          entityId={entityId}
          coachSeats={getValues().coachSeats || 0}
          usedSeats={coachSeats?.usedSeats || 0}
        />
      </>
    );
  },
);

export { CoachSeatsWidget };
